import { useEffect, useRef } from "react";

/**
 * G-106 — Escape closes the TOP overlay, and only the top one.
 *
 * Every overlay used to own a `window` keydown listener that fired whatever
 * else was open. That was harmless while the app never stacked overlays, and
 * became visible the moment the Team sheet started opening modals on top of
 * itself: one Escape closed the modal AND the sheet underneath, dropping the
 * person back to the card list they had just come from.
 *
 * So the overlays share one order. Each mounts a token onto a module-level
 * stack and acts on Escape only while its token is last.
 *
 * Two details are load-bearing:
 *
 *  - the token is pushed once per MOUNT, never on re-render. The stack is
 *    "which overlay is on top", and an overlay that re-registered on every
 *    render would climb over the one actually above it. That is why the
 *    handler reads the callback from a ref instead of listing it as a
 *    dependency — `onClose` is an inline arrow at most call sites, so a
 *    dependency array would re-run this effect constantly.
 *  - the listener stays per-instance rather than one global handler, so an
 *    overlay that unmounts takes its listener with it and nothing has to
 *    reason about a shared subscription.
 *
 * Every overlay in the app now goes through here. `ShopFloorItemCard` and
 * `ShopFloorBoxCard` were moved onto it in the same change and not for
 * neatness: `ShopFloorBoxCard.onOpenItem` opens an item card while the box
 * card stays mounted, so a florist who opened a box, tapped an item and
 * pressed Escape lost both at once. (`ManageFlowerTypesModal` never had a copy
 * — its Escape is an input's `onKeyDown` that cancels a rename, and the dialog
 * around it is a `Modal`.)
 */
const stack: object[] = [];

export function useDismissOnEscape(onClose: () => void): void {
  const latest = useRef(onClose);

  // No dependency array: the ref must hold whatever the current render passed.
  useEffect(() => {
    latest.current = onClose;
  });

  useEffect(() => {
    const token = {};
    stack.push(token);

    const handler = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (stack[stack.length - 1] !== token) return;
      latest.current();
    };

    window.addEventListener("keydown", handler);
    return () => {
      window.removeEventListener("keydown", handler);
      const at = stack.lastIndexOf(token);
      if (at !== -1) stack.splice(at, 1);
    };
  }, []);
}
